/**
 * Consolidated User Tracking System
 * Combines Page Tracking + Activity Tracking + Session Management
 * 
 * Features:
 * - Page visits with time spent and user actions
 * - User interactions (clicks, hovers, scrolls, etc.)
 * - Device information and user identification
 * - Session management integration
 * - Real-time analytics
 */

import { ssoSessionManager } from './sessionManager';
import { userTrackingService } from '@/services/userTrackingService';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface DeviceInfo {
  deviceType: 'desktop' | 'mobile' | 'tablet' | 'unknown';
  os: string;
  browser: string;
  browserVersion: string;
  screenResolution: string;
  viewportSize: string;
  userAgent: string;
  language: string;
  timezone: string;
  isTouchDevice: boolean;
  isRetina: boolean;
  colorDepth: number;
  pixelRatio: number;
}

export interface UserAction {
  type: string;
  timestamp: number;
  element?: string;
  elementId?: string;
  elementClass?: string;
  elementText?: string;
  coordinates?: { x: number; y: number };
  metadata?: Record<string, any>;
}

export interface PageVisit {
  pageId: string;
  pageUrl: string;
  pageTitle: string;
  visitTime: number;
  exitTime?: number;
  timeSpent?: number;
  timeSpentObject?: { m: number; s: number }; // Formatted time object
  referrer?: string;
  userAgent: string;
  viewport: {
    width: number;
    height: number;
  };
  scrollDepth: number;
  interactions: number;
  isActive: boolean;
  // Unique page visit UUID
  pageVisitNumber: string; // Unique UUID for each page visit
  // Navigation tracking
  navigationPath?: string[]; // Array of previous pages in this session
  entryMethod?: 'direct' | 'link' | 'back' | 'forward' | 'refresh';
  exitMethod?: 'navigation' | 'close' | 'refresh' | 'back';
  // User identification
  user_id?: string;
  subscriber_id?: number;
  sessionId?: string;
  deviceId?: string;
  accessToken?: string;
  cookieToken?: string;
  domain?: string;
  siteType?: string;
  // Device information
  deviceInfo?: DeviceInfo;
  // User actions (only essential actions)
  userActions?: {
    clicks: number;
    formInteractions: number;
    downloads: number;
    totalActions: number;
    actionDetails: UserAction[];
  };
  // Performance metrics
  performanceMetrics?: {
    loadTime: number;
    domContentLoaded: number;
    firstContentfulPaint: number;
    largestContentfulPaint: number;
    firstInputDelay: number;
    cumulativeLayoutShift: number;
  };
  // Track previous accumulated time for this page (for revisits)
  previousAccumulatedTime?: number;
}

export interface UserInteraction {
  id: string;
  type: 'click' | 'form_submit' | 'download';
  element: string;
  elementType: string;
  elementId?: string;
  elementClass?: string;
  elementText?: string;
  elementHref?: string;
  timestamp: number;
  pageUrl: string;
  pageTitle: string;
  coordinates?: { x: number; y: number };
  metadata?: Record<string, any>;
  sessionId: string;
  user_id: string;
  subscriber_id: number;
  deviceId?: string;
  accessToken?: string;
  cookieToken?: string;
  token?: string;
  domain?: string;
  siteType?: string;
  deviceInfo?: DeviceInfo;
  pageVisitNumber: string; // Add missing pageVisitNumber property
  // Essential functionality tracking
  functionality?: {
    actionType?: 'submit' | 'download' | 'navigate';
    targetUrl?: string;
    formData?: Record<string, any>;
    formType?: 'bro' | 'nps';
    downloadUrl?: string;
    downloadFileName?: string;
  };
}

export interface UserActivity {
  sessionId: string;
  user_id: string;
  subscriber_id: number;
  deviceId: string;
  accessToken?: string;
  cookieToken?: string;
  domain: string;
  siteType: string;
  startTime: number;
  lastActivity: number;
  totalTimeSpent: number;
  pageVisits: PageVisit[];
  totalPages: number;
  averageTimePerPage: number;
  mostVisitedPage: string;
  engagementScore: number;
  deviceInfo?: DeviceInfo;
}

export interface PageAnalytics {
  pageId: string;
  pageUrl: string;
  totalVisits: number;
  totalTimeSpent: number;
  averageTimeSpent: number;
  uniqueVisitors: number;
  bounceRate: number;
  exitRate: number;
  conversionRate: number;
  engagementRate: number;
  lastVisited: number;
  firstVisited: number;
  // Session metrics
  singlePageSessions: number;
  multiPageSessions: number;
  averagePagesPerSession: number;
  averageSessionDuration: number;
  // User behavior metrics
  returningVisitors: number;
  newVisitors: number;
  averageScrollDepth: number;
  averageInteractions: number;
  // Performance metrics
  averageLoadTime: number;
  averageFirstContentfulPaint: number;
  // User identification
  user_id?: string;
  subscriber_id?: number;
  sessionId?: string;
  deviceId?: string;
  domain?: string;
  siteType?: string;
}

export interface TrackingData {
  pageVisits: PageVisit[];
  userActivity: UserActivity | null;
  pageAnalytics: PageAnalytics[];
  sessionSummary: {
    totalPages: number;
    totalTimeSpent: number;
    averageTimePerPage: number;
    mostVisitedPage: string;
    engagementScore: number;
  };
  timestamp: number;
}

// ============================================================================
// CONSOLIDATED USER TRACKING CLASS
// ============================================================================

class UserTracking {
  // Page tracking
  private currentPage: PageVisit | null = null;
  private pageVisits: PageVisit[] = []; // Changed from Map to Array to store all visits
  private pageStartTime: number = 0;
  private lastActivityTime: number = 0;
  private scrollDepth: number = 0;
  private pageInteractions: number = 0;
  private isPageActive: boolean = true;
  private currentPageTime: number = 0; // Real-time current page time
  private lastDataSync: number = 0; // Last time data was synced to API
  
  // Navigation tracking
  private navigationPath: string[] = [];
  private previousPageUrl: string | null = null;
  
  // Activity tracking
  private interactions: UserInteraction[] = [];
  private deviceInfo: DeviceInfo | null = null;
  
  // Action tracking - only essential actions
  private pageActions: Map<string, UserAction[]> = new Map();
  private actionCounts = {
    clicks: 0,
    formInteractions: 0,
    downloads: 0,
  };
  
  // Timers and intervals
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private inactivityTimer: NodeJS.Timeout | null = null;
  private dataSyncInterval: NodeJS.Timeout | null = null;
  
  // Constants
  private readonly HEARTBEAT_INTERVAL = 30000; // 30 seconds
  private readonly INACTIVITY_THRESHOLD = 30000; // 30 seconds
  private readonly DATA_SYNC_INTERVAL = 60000; // 60 seconds
  private readonly SCROLL_THRESHOLD = 50;
  
  // Activity data
  private activityData: UserActivity | null = null;
  
  // User change tracking
  private currentUserId: string | null = null;
  private currentSubscriberId: number | null = null;
  
  // Cache to prevent redundant server calls
  private userDataCache: { [key: string]: { data: any; timestamp: number } } = {};
  private readonly CACHE_DURATION = 60000; // 1 minute cache
  
  // Analytics tracking
  private pageAnalytics: Map<string, PageAnalytics> = new Map();
  private sessionAnalytics: Map<string, any> = new Map();
  private bounceTracking: Map<string, { isBounce: boolean; timeSpent: number; interactions: number }> = new Map();
  private exitTracking: Map<string, { exitCount: number; totalVisits: number }> = new Map();
  
  // Unique page visit counter
  private pageVisitCounter: number = 0;
  
  constructor() {
    this.detectDeviceInfo();
    this.initializeTracking();
    this.initializeUserChangeTracking();
  }

  // ============================================================================
  // USER CHANGE TRACKING
  // ============================================================================

  private initializeUserChangeTracking(): void {
    if (typeof window === 'undefined') return;

    // Set initial user IDs
    this.updateCurrentUserIds().catch(console.error);

    // Check for user changes every 60 seconds
    setInterval(async () => {
      if (document.visibilityState !== "visible") return;
      await this.checkForUserChange();
    }, 30000);

    // console.log('👤 User change tracking initialized');
  }

  private async updateCurrentUserIds(): Promise<void> {
    const userData = await this.getUserIdentificationData();
    this.currentUserId = userData.user_id;
    this.currentSubscriberId = userData.subscriber_id;
  }

  private async checkForUserChange(): Promise<void> {
    const userData = await this.getUserIdentificationData();
    const newUserId = userData.user_id;
    const newSubscriberId = userData.subscriber_id;

    // Check if user has changed
    if (this.currentUserId !== newUserId || this.currentSubscriberId !== newSubscriberId) {
      // console.log('👤 User change detected:', {
      //   from: { userId: this.currentUserId, subscriberId: this.currentSubscriberId },
      //   to: { userId: newUserId, subscriberId: newSubscriberId }
      // });

      // Store current page data before user change
      this.storeCurrentPageDataBeforeUserChange();

      // Reset tracking for new user
      this.resetTrackingForNewUser();

      // Update current user IDs
      this.currentUserId = newUserId;
      this.currentSubscriberId = newSubscriberId;

      // Start fresh tracking for new user
      this.startFreshTrackingForNewUser();
    }
  }

  private async storeCurrentPageDataBeforeUserChange(): Promise<void> {
    try {
      if (this.currentPage) {
        // console.log('💾 Storing current page data before user change');
        
        // Force exit current page to save data
        this.trackPageExit();
        
        // Store complete tracking data
        await this.storeCompleteTrackingData();
        
        // Force flush any pending data
        const host = this.currentPage.domain || window.location.hostname;
        const token = this.getSubscriberLoginToken() || this.currentPage.accessToken || '';
        await userTrackingService.forceFlush(host, token);
      }
    } catch (error) {
      console.error('❌ Error storing data before user change:', error);
    }
  }

  private resetTrackingForNewUser(): void {
    // console.log('🔄 Resetting tracking for new user');
    
    // Clear current page
    this.currentPage = null;
    
    // Clear page visits array
    this.pageVisits = [];
    
    // Clear interactions
    this.interactions = [];
    
    // Clear page actions
    this.pageActions.clear();
    
    // Reset action counts
    this.resetActionCounts();
    
    // Clear activity data
    this.activityData = null;
    
    // Reset navigation path
    this.navigationPath = [];
    this.previousPageUrl = null;
    
    // Reset timers
    this.pageStartTime = 0;
    this.lastActivityTime = 0;
    this.scrollDepth = 0;
    this.pageInteractions = 0;
    this.isPageActive = true;
    this.currentPageTime = 0;
    this.lastDataSync = 0;
    
    // Reset page visit counter for new user
    this.pageVisitCounter = 0;
  }

  private startFreshTrackingForNewUser(): void {
    // console.log('🆕 Starting fresh tracking for new user');
    
    // Start tracking the current page for the new user
    this.trackPageEntry();
    
    // Update activity data
    this.updateActivityData();
  }

  // ============================================================================
  // DEVICE DETECTION
  // ============================================================================

  private detectDeviceInfo(): void {
    if (typeof window === 'undefined') return;

    const userAgent = navigator.userAgent;
    const screen = window.screen;
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight
    };

    let deviceType: 'desktop' | 'mobile' | 'tablet' | 'unknown' = 'unknown';
    if (/Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
      if (/iPad|Android.*Tablet|Windows.*Touch/i.test(userAgent)) {
        deviceType = 'tablet';
      } else {
        deviceType = 'mobile';
      }
    } else {
      deviceType = 'desktop';
    }

    let os = 'Unknown';
    if (/Windows/i.test(userAgent)) os = 'Windows';
    else if (/Mac/i.test(userAgent)) os = 'macOS';
    else if (/Linux/i.test(userAgent)) os = 'Linux';
    else if (/Android/i.test(userAgent)) os = 'Android';
    else if (/iPhone|iPad|iPod/i.test(userAgent)) os = 'iOS';

    let browser = 'Unknown';
    let browserVersion = 'Unknown';
    if (/Chrome/i.test(userAgent)) {
      browser = 'Chrome';
      const match = userAgent.match(/Chrome\/(\d+)/);
      browserVersion = match ? match[1] : 'Unknown';
    } else if (/Firefox/i.test(userAgent)) {
      browser = 'Firefox';
      const match = userAgent.match(/Firefox\/(\d+)/);
      browserVersion = match ? match[1] : 'Unknown';
    } else if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)) {
      browser = 'Safari';
      const match = userAgent.match(/Version\/(\d+)/);
      browserVersion = match ? match[1] : 'Unknown';
    } else if (/Edge/i.test(userAgent)) {
      browser = 'Edge';
      const match = userAgent.match(/Edge\/(\d+)/);
      browserVersion = match ? match[1] : 'Unknown';
    }

    this.deviceInfo = {
      deviceType,
      os,
      browser,
      browserVersion,
      screenResolution: `${screen.width}x${screen.height}`,
      viewportSize: `${viewport.width}x${viewport.height}`,
      userAgent,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      isTouchDevice: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
      isRetina: window.devicePixelRatio > 1,
      colorDepth: screen.colorDepth,
      pixelRatio: window.devicePixelRatio
    };

    // console.log('📱 Device info detected:', this.deviceInfo);
  }

  // ============================================================================
  // USER IDENTIFICATION
  // ============================================================================

  private async getUserIdentificationData() {
    const session = ssoSessionManager.getCurrentSession();
    const cacheKey = `user_data_${session?.subscriber_id || 'anonymous'}`;
    
    // Check cache first
    const cached = this.userDataCache[cacheKey];
    if (cached && (Date.now() - cached.timestamp) < this.CACHE_DURATION) {
      // console.log('🔍 Using cached user identification data');
      return cached.data;
    }
    
    // Get subscriber login token from various sources
    const subscriberToken = this.getSubscriberLoginToken();
    
    // Get consistent user_id based on subscriber_id (same user_id across all devices)
    const consistentUserId = await this.getConsistentUserId(session?.subscriber_id);
    
    const userData = {
      user_id: consistentUserId,
      subscriber_id: session?.subscriber_id || 0,
      sessionId: session?.sessionId || this.getOrGenerateSessionId(),
      deviceId: session?.deviceId || this.getOrGenerateDeviceId(),
      accessToken: subscriberToken || session?.accessToken || null,
      cookieToken: this.getOrGenerateCookieToken(),
      domain: session?.domain || window.location.hostname,
      siteType: session?.siteType || 'UNKNOWN'
    };

    // Cache the result
    this.userDataCache[cacheKey] = {
      data: userData,
      timestamp: Date.now()
    };

    // console.log('🔍 User identification data:', userData);
    return userData;
  }

  private getOrGenerateUserId(): string {
    const stored = localStorage.getItem('pm_user_id');
    if (stored) return stored;
    
    const userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('pm_user_id', userId);
    return userId;
  }

  private async getConsistentUserId(subscriber_id?: number): Promise<string> {
    // If no subscriber_id, generate a device-based user_id
    if (!subscriber_id || subscriber_id <= 0) {
      return this.getOrGenerateDeviceBasedUserId();
    }

    // For logged-in users, get consistent user_id based on subscriber_id
    const userKey = `pm_user_id_${subscriber_id}`;
    let userId = localStorage.getItem(userKey);
    
    if (!userId) {
      // Try sessionStorage as backup
      userId = sessionStorage.getItem(userKey);
      if (userId) {
        // console.log(`🔗 Retrieved user_id from sessionStorage for subscriber_id ${subscriber_id}:`, userId);
        // Store in localStorage for persistence
        localStorage.setItem(userKey, userId);
      }
    }
    
    if (!userId) {
      // Try to get user_id from server/API for cross-device sync
      const serverUserId = await this.getUserIdFromServer(subscriber_id);
      if (serverUserId) {
        userId = serverUserId;
        // console.log(`🌐 Retrieved user_id from server for subscriber_id ${subscriber_id}:`, userId);
        // Store in both localStorage and sessionStorage
        localStorage.setItem(userKey, userId);
        sessionStorage.setItem(userKey, userId);
      }
    }
    
    if (!userId) {
      // Generate new UUID for this subscriber
      userId = this.generateUUID();
      localStorage.setItem(userKey, userId);
      sessionStorage.setItem(userKey, userId);
      // console.log(`🔗 Generated new user_id ${userId} for subscriber_id ${subscriber_id}`);
      
      // Send new user_id to server for cross-device sync
      this.syncUserIdToServer(subscriber_id, userId);
    } else {
      // console.log(`🔗 Retrieved existing user_id ${userId} for subscriber_id ${subscriber_id}`);
      
      // Sync this user_id to server to ensure consistency
      this.syncUserIdToServer(subscriber_id, userId);
    }
    
    return userId;
  }

  private getOrGenerateDeviceBasedUserId(): string {
    // For anonymous users, use device-based ID
    let stored = localStorage.getItem('pm_device_user_id');
    if (stored) {
      // console.log('🔧 Retrieved existing device-based user_id:', stored);
      return stored;
    }
    
    // Try sessionStorage as backup
    stored = sessionStorage.getItem('pm_device_user_id');
    if (stored) {
      // console.log('🔧 Retrieved device-based user_id from sessionStorage:', stored);
      // Store in localStorage for persistence
      localStorage.setItem('pm_device_user_id', stored);
      return stored;
    }
    
    // Generate new device-based user ID
    const deviceUserId = this.generateUUID();
    localStorage.setItem('pm_device_user_id', deviceUserId);
    sessionStorage.setItem('pm_device_user_id', deviceUserId);
    // console.log('🔧 Generated new device-based user_id:', deviceUserId);
    return deviceUserId;
  }

  private getOrGenerateSessionId(): string {
    const stored = localStorage.getItem('pm_session_id');
    if (stored) return stored;
    
    const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('pm_session_id', sessionId);
    return sessionId;
  }

  private getOrGenerateDeviceId(): string {
    // Try to get existing device ID from localStorage first
    let stored = localStorage.getItem('pm_device_id');
    if (stored) {
      // console.log('🔧 Retrieved existing device_id:', stored);
      return stored;
    }
    
    // Try to get from sessionStorage as backup
    stored = sessionStorage.getItem('pm_device_id');
    if (stored) {
      // console.log('🔧 Retrieved device_id from sessionStorage:', stored);
      // Store in localStorage for persistence
      localStorage.setItem('pm_device_id', stored);
      return stored;
    }
    
    // Generate new device ID using device fingerprint
    const deviceId = this.generateDeviceFingerprint();
    localStorage.setItem('pm_device_id', deviceId);
    sessionStorage.setItem('pm_device_id', deviceId);
    // console.log('🔧 Generated new persistent device_id:', deviceId);
    return deviceId;
  }

  private getOrGenerateCookieToken(): string {
    const stored = localStorage.getItem('pm_cookie_token');
    if (stored) return stored;
    
    const token = btoa(`${Date.now()}.${Math.random().toString(36).substr(2, 9)}`);
    localStorage.setItem('pm_cookie_token', token);
    return token;
  }

  private getSubscriberLoginToken(): string | null {
    // Priority order for getting subscriber login token:
    // 1. peoples_token from localStorage (most reliable)
    // 2. peoples_token from cookies
    // 3. Other SSO tokens from localStorage
    // 4. Other SSO tokens from cookies
    
    // Get session data first
    const session = ssoSessionManager.getCurrentSession();
    
    // Check peoples_token in localStorage first
    const peoplesToken = session?.accessToken || localStorage.getItem('peoples_token');
    if (peoplesToken && peoplesToken.trim() !== '') {
      // console.log('🔐 Found peoples_token in localStorage');
      return peoplesToken;
    }

    // Check peoples_token in cookies
    const cookiePeoplesToken = this.getCookieValue('peoples_token');
    if (cookiePeoplesToken && cookiePeoplesToken.trim() !== '') {
      // console.log('🔐 Found peoples_token in cookies');
      return cookiePeoplesToken;
    }

    // Check other SSO tokens in localStorage
    const ssoTokens = ['sso_token', 'auth_token', 'access_token', 'jwt_token'];
    for (const tokenName of ssoTokens) {
      const token = localStorage.getItem(tokenName);
      if (token && token.trim() !== '') {
        // console.log(`🔐 Found ${tokenName} in localStorage`);
        return token;
      }
    }

    // Check other SSO tokens in cookies
    for (const tokenName of ssoTokens) {
      const token = this.getCookieValue(tokenName);
      if (token && token.trim() !== '') {
        // console.log(`🔐 Found ${tokenName} in cookies`);
        return token;
      }
    }

    // console.log('🔐 No subscriber login token found');
    return null;
  }

  private getCookieValue(name: string): string | null {
    if (typeof document === 'undefined') return null;
    
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }

  // ============================================================================
  // PAGE TRACKING
  // ============================================================================

  public async trackPageEntry(pageUrl?: string, pageTitle?: string): Promise<void> {
    const url = pageUrl || window.location.href;
    const title = pageTitle || document.title;
    const pageId = this.generatePageId(url);

    // Check if this is the same page (avoid duplicate tracking)
    if (this.currentPage && this.currentPage.pageUrl === url) {
      // console.log(`📊 Same page detected, skipping duplicate tracking: ${url}`);
      return;
    }

    // Check if this page was visited before and preserve accumulated time
    // let previousAccumulatedTime = 0;
    // const existingPageIndex = this.pageVisits.findIndex(visit => visit.pageUrl === url);
    // if (existingPageIndex !== -1) {
    //   previousAccumulatedTime = this.pageVisits[existingPageIndex].timeSpent || 0;
    //   console.log(`📊 Revisiting page: ${url} - Previous accumulated time: ${Math.round(previousAccumulatedTime / 1000)}s`);
    // }

    // Save previous page if exists
    if (this.currentPage) {
      this.trackPageExit();
    }

    // Track navigation path
    this.previousPageUrl = this.currentPage?.pageUrl || null;
    if (this.previousPageUrl && this.previousPageUrl !== url) {
      this.navigationPath.push(this.previousPageUrl);
    }

    // Determine entry method
    const entryMethod = this.determineEntryMethod();

    // Get user identification data
    const userData = await this.getUserIdentificationData();

    // Capture performance metrics
    const performanceMetrics = this.capturePerformanceMetrics();

    // Generate unique UUID for this page visit
    const pageVisitUUID = this.generateUUID();

    // Create new page visit
    this.currentPage = {
      pageId,
      pageUrl: url,
      pageTitle: title,
      visitTime: Date.now(),
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      scrollDepth: 0,
      interactions: 0,
      isActive: true,
      // Unique page visit UUID
      pageVisitNumber: pageVisitUUID,
      referrer: this.previousPageUrl || document.referrer || undefined,
      // Navigation tracking
      navigationPath: [...this.navigationPath],
      entryMethod: entryMethod,
      // User identification data
      user_id: userData.user_id,
      subscriber_id: userData.subscriber_id,
      sessionId: userData.sessionId,
      deviceId: userData.deviceId,
      accessToken: userData.accessToken || undefined,
      cookieToken: userData.cookieToken,
      domain: userData.domain,
      siteType: userData.siteType,
      // Device information
      deviceInfo: this.deviceInfo || undefined,
      // Performance metrics
      performanceMetrics: performanceMetrics,
      // User actions (only essential actions)
      userActions: {
        clicks: 0,
        formInteractions: 0,
        downloads: 0,
        totalActions: 0,
        actionDetails: []
      },
      // Each visit is independent (no previous accumulated time)
      previousAccumulatedTime: 0,
    };

    this.pageStartTime = Date.now();
    this.lastActivityTime = Date.now();
    this.scrollDepth = 0;
    this.pageInteractions = 0;
    
    // Reset action counts for new page
    this.resetActionCounts();

    // Current page is now being tracked (will be added to array when exiting)

    // Update activity data
    this.updateActivityData();

    // Reset inactivity timer
    this.resetInactivityTimer();

    // console.log(`📊 Page tracked: ${title} (${url}) - Visit UUID: ${pageVisitUUID} - Entry: ${entryMethod} - From: ${this.previousPageUrl || 'direct'}`);
  }

  public trackPageExit(): void {
    if (!this.currentPage) return;

    const exitTime = Date.now();
    const timeSpent = exitTime - this.currentPage.visitTime;

    this.currentPage.exitTime = exitTime;
    this.currentPage.timeSpent = timeSpent;
    this.currentPage.isActive = false;

    // Update user actions with final counts
    this.currentPage.userActions = this.getCurrentPageActions() || undefined;
    // if (existingPageIndex !== -1) {
    //   // Update existing entry with new time and data
    //   const existingPage = this.pageVisits[existingPageIndex];
    //   const previousTime = existingPage.timeSpent || 0;
    //   const currentSessionTime = timeSpent;
    //   existingPage.timeSpent = previousTime + currentSessionTime; // Add current session time to previous accumulated time
    //   existingPage.exitTime = exitTime;
    //   existingPage.visitTime = Math.min(existingPage.visitTime, this.currentPage.visitTime); // Keep earliest visit time
    //   existingPage.userActions = this.currentPage.userActions;
    //   existingPage.interactions = this.currentPage.interactions;
      
    //   console.log(`📊 Page exit (updated): ${this.currentPage.pageTitle} - Previous: ${Math.round(previousTime / 1000)}s + Current: ${Math.round(currentSessionTime / 1000)}s = Total: ${Math.round(existingPage.timeSpent / 1000)}s`);
    // } else {
    //   // Add new entry to array
    //   this.pageVisits.push({ ...this.currentPage });
    //   console.log(`📊 Page exit (new): ${this.currentPage.pageTitle} - Time spent: ${Math.round(timeSpent / 1000)}s`);
    // }


    // Always create a new entry for each page visit (no accumulation)
    this.pageVisits.push({ ...this.currentPage });
    // console.log(`📊 Page exit (Visit UUID: ${this.currentPage.pageVisitNumber}): ${this.currentPage.pageTitle} - Time spent: ${Math.round(timeSpent / 1000)}s`);

    // Store page visit data via API
    this.storePageVisitData();

    // Update activity data
    this.updateActivityData();
  }

  // ============================================================================
  // ACTIVITY TRACKING
  // ============================================================================

  public async trackUserAction(actionType: string, element?: HTMLElement, coordinates?: { x: number; y: number }, metadata?: Record<string, any>): Promise<void> {
    if (!this.currentPage) return;

    const pageId = this.currentPage.pageId;
    
    // Initialize actions array for this page if not exists
    if (!this.pageActions.has(pageId)) {
      this.pageActions.set(pageId, []);
    }

    // Create action record
    const action: UserAction = {
      type: actionType,
      timestamp: Date.now(),
      element: element?.tagName?.toLowerCase(),
      elementId: element?.id,
      elementClass: element?.className,
      elementText: element?.textContent?.substring(0, 100),
      coordinates: coordinates,
      metadata: metadata
    };

    // Add to page actions
    const actions = this.pageActions.get(pageId)!;
    actions.push(action);

    // Update action counts - only track essential actions
    switch (actionType) {
      case 'click':
        this.actionCounts.clicks++;
        break;
      case 'form_submit':
        this.actionCounts.formInteractions++;
        break;
      case 'download':
        this.actionCounts.downloads++;
        break;
      default:
        // Skip other actions
        break;
    }

    // Update current page interactions count
    this.currentPage.interactions = this.getTotalActions();

    // Also create a full interaction record
    const userData = await this.getUserIdentificationData();
    const interaction: UserInteraction = {
      id: this.generateId(),
      type: actionType as any,
      element: element?.tagName?.toLowerCase() || 'unknown',
      elementType: element?.tagName?.toLowerCase() || 'unknown',
      elementId: element?.id,
      elementClass: element?.className,
      elementText: element?.textContent?.substring(0, 100),
      elementHref: (element as HTMLAnchorElement)?.href,
      timestamp: Date.now(),
      pageUrl: window.location.href,
      pageTitle: document.title,
      coordinates: coordinates,
      metadata: metadata,
      sessionId: userData.sessionId,
      user_id: userData.user_id,
      subscriber_id: userData.subscriber_id,
      deviceId: userData.deviceId,
      accessToken: userData.accessToken || undefined,
      cookieToken: userData.cookieToken,
      domain: userData.domain,
      siteType: userData.siteType,
      deviceInfo: this.deviceInfo || undefined,
      pageVisitNumber: this.currentPage?.pageVisitNumber || '',
      // Enhanced functionality tracking
      functionality: this.analyzeElementFunctionality(element, actionType, metadata)
    };

    this.interactions.push(interaction);

    // Store user interaction data via API
    this.storeUserInteractionData(interaction);

    // console.log(`🎯 User action tracked: ${actionType} on page ${pageId}`, action);
  }

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  private initializeTracking(): void {
    if (typeof window === 'undefined') return;

    // Track clicks (only essential clicks)
    document.addEventListener('click', this.handleClick.bind(this), true);

    // Track form submissions (Bro and NPS forms)
    document.addEventListener('submit', this.handleFormSubmit.bind(this), true);

    // Track downloads
    document.addEventListener('click', this.handleDownload.bind(this), true);

    // Start heartbeat for page time tracking
    this.startHeartbeat();

    // Start data sync
    this.startDataSync();

    // console.log('🎯 Simplified user tracking initialized - tracking clicks, downloads, forms, and page visits only');
  }

  private handleClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target) return;

    // Only track meaningful clicks (links, buttons, form elements)
    const isLink = target.tagName === 'A' || target.closest('a');
    const isButton = target.tagName === 'BUTTON' || target.closest('button');
    const isFormElement = ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName);
    const isClickable = target.getAttribute('role') === 'button' || 
                       target.getAttribute('onclick') || 
                       target.style.cursor === 'pointer';

    if (!isLink && !isButton && !isFormElement && !isClickable) {
      return; // Skip tracking non-meaningful clicks
    }

    // Get the URL if it's a link
    let clickedUrl = null;
    if (isLink) {
      const link = target.tagName === 'A' ? target as HTMLAnchorElement : target.closest('a') as HTMLAnchorElement;
      clickedUrl = link?.href;
    }

    this.trackUserAction('click', target, { x: event.clientX, y: event.clientY }, {
      button: event.button,
      ctrlKey: event.ctrlKey,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
      metaKey: event.metaKey,
      clickedUrl: clickedUrl,
      elementText: target.textContent?.substring(0, 100),
      elementType: target.tagName.toLowerCase()
    });

    this.updateLastActivity();
  }


  private handleDownload(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target) return;

    // Check if it's a download link
    const isDownload = target.tagName === 'A' && 
                      (target.getAttribute('download') || 
                       target.getAttribute('href')?.includes('.pdf') ||
                       target.getAttribute('href')?.includes('.doc') ||
                       target.getAttribute('href')?.includes('.zip') ||
                       target.textContent?.toLowerCase().includes('download'));

    if (isDownload) {
      const link = target as HTMLAnchorElement;
      this.trackUserAction('download', target, { x: event.clientX, y: event.clientY }, {
        downloadUrl: link.href,
        downloadFileName: link.getAttribute('download'),
        elementText: target.textContent?.substring(0, 100)
      });
      this.updateLastActivity();
    }
  }

  private handleFormSubmit(event: Event): void {
    const target = event.target as HTMLFormElement;
    if (!target) return;

    // Only track Bro and NPS forms
    const formId = target.id?.toLowerCase() || '';
    const formClass = target.className?.toLowerCase() || '';
    const formAction = target.action?.toLowerCase() || '';
    const formName = target.name?.toLowerCase() || '';

    const isBroForm = formId.includes('bro') || formClass.includes('bro') || 
                     formAction.includes('bro') || formName.includes('bro');
    const isNpsForm = formId.includes('nps') || formClass.includes('nps') || 
                     formAction.includes('nps') || formName.includes('nps');

    if (isBroForm || isNpsForm) {
      this.trackUserAction('form_submit', target, undefined, {
        formId: target.id,
        formClass: target.className,
        formAction: target.action,
        formMethod: target.method,
        formType: isBroForm ? 'bro' : 'nps',
        formData: this.extractFormData(target)
      });
      this.updateLastActivity();
    }
  }

  private extractFormData(form: HTMLFormElement): Record<string, any> {
    const formData: Record<string, any> = {};
    const formElements = form.elements;
    
    for (let i = 0; i < formElements.length; i++) {
      const field = formElements[i] as HTMLInputElement;
      if (field.name && field.type !== 'submit' && field.type !== 'button') {
        formData[field.name] = field.value;
      }
    }
    
    return formData;
  }

  // ============================================================================
  // ANALYTICS CALCULATIONS
  // ============================================================================

  private calculateBounceRate(pageId: string, pageVisits: PageVisit[]): number {
    const pageVisitsForPage = pageVisits.filter(visit => visit.pageId === pageId);
    if (pageVisitsForPage.length === 0) return 0;

    let bounceCount = 0;
    const bounceThreshold = 30000; // 30 seconds
    const interactionThreshold = 1; // At least 1 interaction

    pageVisitsForPage.forEach(visit => {
      const timeSpent = visit.timeSpent || 0;
      const interactions = visit.interactions || 0;
      
      // Consider it a bounce if:
      // 1. Time spent is less than 30 seconds AND
      // 2. No meaningful interactions
      if (timeSpent < bounceThreshold && interactions < interactionThreshold) {
        bounceCount++;
      }
    });

    return (bounceCount / pageVisitsForPage.length) * 100;
  }

  private calculateExitRate(pageId: string, pageVisits: PageVisit[]): number {
    const pageVisitsForPage = pageVisits.filter(visit => visit.pageId === pageId);
    if (pageVisitsForPage.length === 0) return 0;

    // Count how many times this page was the last page in a session
    let exitCount = 0;
    const allSessions = this.getUniqueSessions(pageVisits);
    
    allSessions.forEach(sessionPages => {
      if (sessionPages.length > 0) {
        const lastPage = sessionPages[sessionPages.length - 1];
        if (lastPage.pageId === pageId) {
          exitCount++;
        }
      }
    });

    return (exitCount / pageVisitsForPage.length) * 100;
  }

  private calculateEngagementRate(pageId: string, pageVisits: PageVisit[]): number {
    const pageVisitsForPage = pageVisits.filter(visit => visit.pageId === pageId);
    if (pageVisitsForPage.length === 0) return 0;

    let engagedVisits = 0;
    const engagementThreshold = 60000; // 1 minute
    const interactionThreshold = 2; // At least 2 interactions

    pageVisitsForPage.forEach(visit => {
      const timeSpent = visit.timeSpent || 0;
      const interactions = visit.interactions || 0;
      const scrollDepth = visit.scrollDepth || 0;
      
      // Consider it engaged if:
      // 1. Time spent is more than 1 minute OR
      // 2. More than 2 interactions OR
      // 3. Scrolled more than 50% of the page
      if (timeSpent > engagementThreshold || 
          interactions > interactionThreshold || 
          scrollDepth > 50) {
        engagedVisits++;
      }
    });

    return (engagedVisits / pageVisitsForPage.length) * 100;
  }

  private getUniqueSessions(pageVisits: PageVisit[]): PageVisit[][] {
    const sessions = new Map<string, PageVisit[]>();
    
    pageVisits.forEach(visit => {
      const sessionId = visit.sessionId || 'unknown';
      if (!sessions.has(sessionId)) {
        sessions.set(sessionId, []);
      }
      sessions.get(sessionId)!.push(visit);
    });

    // Sort visits within each session by visit time
    sessions.forEach(sessionPages => {
      sessionPages.sort((a, b) => a.visitTime - b.visitTime);
    });

    return Array.from(sessions.values());
  }

  private calculateSessionMetrics(pageVisits: PageVisit[]): {
    singlePageSessions: number;
    multiPageSessions: number;
    averagePagesPerSession: number;
    averageSessionDuration: number;
  } {
    const sessions = this.getUniqueSessions(pageVisits);
    
    let singlePageSessions = 0;
    let multiPageSessions = 0;
    let totalPages = 0;
    let totalDuration = 0;

    sessions.forEach(sessionPages => {
      totalPages += sessionPages.length;
      
      if (sessionPages.length === 1) {
        singlePageSessions++;
      } else {
        multiPageSessions++;
      }

      // Calculate session duration
      if (sessionPages.length > 0) {
        const firstPage = sessionPages[0];
        const lastPage = sessionPages[sessionPages.length - 1];
        const sessionDuration = (lastPage.exitTime || Date.now()) - firstPage.visitTime;
        totalDuration += sessionDuration;
      }
    });

    return {
      singlePageSessions,
      multiPageSessions,
      averagePagesPerSession: sessions.length > 0 ? totalPages / sessions.length : 0,
      averageSessionDuration: sessions.length > 0 ? totalDuration / sessions.length : 0
    };
  }

  private calculatePerformanceMetrics(pageVisits: PageVisit[]): {
    averageLoadTime: number;
    averageFirstContentfulPaint: number;
  } {
    const validVisits = pageVisits.filter(visit => visit.performanceMetrics);
    
    if (validVisits.length === 0) {
      return { averageLoadTime: 0, averageFirstContentfulPaint: 0 };
    }

    const totalLoadTime = validVisits.reduce((sum, visit) => 
      sum + (visit.performanceMetrics?.loadTime || 0), 0);
    const totalFCP = validVisits.reduce((sum, visit) => 
      sum + (visit.performanceMetrics?.firstContentfulPaint || 0), 0);

    return {
      averageLoadTime: totalLoadTime / validVisits.length,
      averageFirstContentfulPaint: totalFCP / validVisits.length
    };
  }

  private calculateComprehensivePageAnalytics(pageVisits: PageVisit[]): PageAnalytics[] {
    // Group visits by page ID
    const pageGroups = new Map<string, PageVisit[]>();
    
    pageVisits.forEach(visit => {
      if (!pageGroups.has(visit.pageId)) {
        pageGroups.set(visit.pageId, []);
      }
      pageGroups.get(visit.pageId)!.push(visit);
    });

    const analytics: PageAnalytics[] = [];
    const sessionMetrics = this.calculateSessionMetrics(pageVisits);
    const performanceMetrics = this.calculatePerformanceMetrics(pageVisits);

    pageGroups.forEach((visits, pageId) => {
      const firstVisit = visits.reduce((earliest, visit) => 
        visit.visitTime < earliest.visitTime ? visit : earliest);
      const lastVisit = visits.reduce((latest, visit) => 
        visit.visitTime > latest.visitTime ? visit : latest);

      const totalTimeSpent = visits.reduce((sum, visit) => sum + (visit.timeSpent || 0), 0);
      const totalInteractions = visits.reduce((sum, visit) => sum + visit.interactions, 0);
      const totalScrollDepth = visits.reduce((sum, visit) => sum + visit.scrollDepth, 0);

      // Calculate unique visitors (by user_id)
      const uniqueVisitors = new Set(visits.map(visit => visit.user_id)).size;

      // Calculate returning vs new visitors
      const userVisitCounts = new Map<string, number>();
      visits.forEach(visit => {
        const userId = visit.user_id || 'anonymous';
        userVisitCounts.set(userId, (userVisitCounts.get(userId) || 0) + 1);
      });

      const returningVisitors = Array.from(userVisitCounts.values()).filter(count => count > 1).length;
      const newVisitors = uniqueVisitors - returningVisitors;

      const analyticsData: PageAnalytics = {
        pageId,
        pageUrl: firstVisit.pageUrl,
        totalVisits: visits.length,
        totalTimeSpent,
        averageTimeSpent: totalTimeSpent / visits.length,
        uniqueVisitors,
        bounceRate: this.calculateBounceRate(pageId, pageVisits),
        exitRate: this.calculateExitRate(pageId, pageVisits),
        conversionRate: 0, // Would need to define conversion events
        engagementRate: this.calculateEngagementRate(pageId, pageVisits),
        lastVisited: lastVisit.visitTime,
        firstVisited: firstVisit.visitTime,
        // Session metrics
        singlePageSessions: sessionMetrics.singlePageSessions,
        multiPageSessions: sessionMetrics.multiPageSessions,
        averagePagesPerSession: sessionMetrics.averagePagesPerSession,
        averageSessionDuration: sessionMetrics.averageSessionDuration,
        // User behavior metrics
        returningVisitors,
        newVisitors,
        averageScrollDepth: totalScrollDepth / visits.length,
        averageInteractions: totalInteractions / visits.length,
        // Performance metrics
        averageLoadTime: performanceMetrics.averageLoadTime,
        averageFirstContentfulPaint: performanceMetrics.averageFirstContentfulPaint,
        // User identification (from first visit)
        user_id: firstVisit.user_id,
        subscriber_id: firstVisit.subscriber_id,
        sessionId: firstVisit.sessionId,
        deviceId: firstVisit.deviceId,
        domain: firstVisit.domain,
        siteType: firstVisit.siteType
      };

      analytics.push(analyticsData);
    });

    return analytics;
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  private generateUUID(): string {
    // Generate a UUID v4
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  private generateDeviceFingerprint(): string {
    // Create a consistent device fingerprint based on browser characteristics
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx?.fillText('Device fingerprint', 2, 2);
    const fingerprint = canvas.toDataURL();
    
    // Combine multiple browser characteristics for uniqueness
    const userAgent = navigator.userAgent;
    const language = navigator.language;
    const platform = navigator.platform;
    const screenResolution = `${screen.width}x${screen.height}`;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Create a hash of these characteristics
    const fingerprintData = `${userAgent}-${language}-${platform}-${screenResolution}-${timezone}-${fingerprint}`;
    
    // Generate a consistent hash
    let hash = 0;
    for (let i = 0; i < fingerprintData.length; i++) {
      const char = fingerprintData.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Convert to positive hex string and add timestamp for uniqueness
    const positiveHash = Math.abs(hash).toString(16);
    const timestamp = Date.now().toString(36);
    
    return `device_${positiveHash}_${timestamp}`;
  }

  private async getUserIdFromServer(subscriber_id: number): Promise<string | null> {
    try {
      const session = ssoSessionManager.getCurrentSession();
      const token = this.getSubscriberLoginToken() || session?.accessToken;
      
      if (!token) {
        // console.log('🔐 No token available for server sync');
        return null;
      }

      // Use userTrackingService to get user_id from server
      const host = window.location.hostname;
      const device_id = this.getOrGenerateDeviceId();
      
      // Use the new service method
      const userId = await userTrackingService.getUserId(subscriber_id, device_id, host, token);
      
      // console.log(`🌐 Requested user_id from server for subscriber_id: ${subscriber_id}`);
      
      return userId;
    } catch (error) {
      console.error('🌐 Error fetching user_id from server:', error);
    }
    
    return null;
  }

  private async syncUserIdToServer(subscriber_id: number, user_id: string): Promise<void> {
    try {
      const session = ssoSessionManager.getCurrentSession();
      const token = this.getSubscriberLoginToken() || session?.accessToken;
      
      if (!token) {
        // console.log('🔐 No token available for server sync');
        return;
      }

      // Use userTrackingService to sync user_id to server
      const host = window.location.hostname;
      const device_id = this.getOrGenerateDeviceId();
      
      // Use the new service method
      await userTrackingService.syncUserId(subscriber_id, user_id, device_id, host, token);
      
      // console.log(`🌐 Successfully queued user_id sync ${user_id} to server for subscriber_id ${subscriber_id}`);
    } catch (error) {
      console.error('🌐 Error syncing user_id to server:', error);
    }
  }


  private generatePageId(url: string): string {
    return btoa(url).replace(/[^a-zA-Z0-9]/g, '').substring(0, 10);
  }

  private determineEntryMethod(): 'direct' | 'link' | 'back' | 'forward' | 'refresh' {
    if (this.previousPageUrl === null) {
      return 'direct';
    }
    
    // Check if it's a refresh
    if (performance.navigation && performance.navigation.type === 1) {
      return 'refresh';
    }
    
    // Check if it's back/forward navigation
    if (performance.navigation && (performance.navigation.type === 2 || performance.navigation.type === 3)) {
      return performance.navigation.type === 2 ? 'back' : 'forward';
    }
    
    // Default to link navigation
    return 'link';
  }

  private capturePerformanceMetrics() {
    if (typeof window === 'undefined' || !window.performance) {
      return undefined;
    }

    try {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      const paint = performance.getEntriesByType('paint');
      
      const firstContentfulPaint = paint.find(entry => entry.name === 'first-contentful-paint');
      let largestContentfulPaint = 0
      if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const lastEntry = entries[entries.length - 1];
        largestContentfulPaint = Math.round(lastEntry.startTime);
      });

      observer.observe({
        type: 'largest-contentful-paint',
        buffered: true
      });
    }
      
      return {
        loadTime: navigation ? Math.round(navigation.loadEventEnd - navigation.loadEventStart) : 0,
        domContentLoaded: navigation ? Math.round(navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart) : 0,
        firstContentfulPaint: firstContentfulPaint ? Math.round(firstContentfulPaint.startTime) : 0,
        // largestContentfulPaint: largestContentfulPaint ? Math.round(largestContentfulPaint.startTime) : 0,
        largestContentfulPaint,
        firstInputDelay: 0, // Would need to be measured separately
        cumulativeLayoutShift: 0 // Would need to be measured separately
      };
    } catch (error) {
      console.warn('Failed to capture performance metrics:', error);
      return undefined;
    }
  }

  private analyzeElementFunctionality(element: HTMLElement | undefined, actionType: string, metadata?: Record<string, any>) {
    if (!element) return undefined;

    const functionality: any = {};

    // For clicks - determine if it's navigation
    if (actionType === 'click') {
      const href = (element as HTMLAnchorElement)?.href;
      if (href) {
        functionality.actionType = 'navigate';
        functionality.targetUrl = href;
      }
    }

    // For downloads
    if (actionType === 'download' && metadata) {
      functionality.actionType = 'download';
      functionality.downloadUrl = metadata.downloadUrl;
      functionality.downloadFileName = metadata.downloadFileName;
    }

    // For form submissions - track form type and data
    if (actionType === 'form_submit' && metadata) {
      functionality.actionType = 'submit';
      functionality.formType = metadata.formType;
      if (metadata.formData) {
        functionality.formData = metadata.formData;
      }
    }

    return Object.keys(functionality).length > 0 ? functionality : undefined;
  }

  private generateId(): string {
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getTotalActions(): number {
    return Object.values(this.actionCounts).reduce((sum, count) => sum + count, 0);
  }

  private getCurrentPageActions() {
    if (!this.currentPage) return null;

    const pageId = this.currentPage.pageId;
    const actions = this.pageActions.get(pageId) || [];

    return {
      clicks: this.actionCounts.clicks,
      formInteractions: this.actionCounts.formInteractions,
      downloads: this.actionCounts.downloads,
      totalActions: this.getTotalActions(),
      actionDetails: actions
    };
  }

  private resetActionCounts(): void {
    this.actionCounts = {
      clicks: 0,
      formInteractions: 0,
      downloads: 0,
    };
  }

  private updateLastActivity(): void {
    this.lastActivityTime = Date.now();
    this.resetInactivityTimer();
  }

  private resetInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }

    this.inactivityTimer = setTimeout(() => {
      if (this.currentPage) {
        this.currentPage.isActive = false;
      }
      this.isPageActive = false;
    }, this.INACTIVITY_THRESHOLD);
  }

  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(() => {
      if (document.visibilityState !== "visible") {
        return;
      }
      if (this.currentPage) {
        // Update real-time current page time
        this.currentPageTime = Date.now() - this.pageStartTime;
        
        // Update the current page's timeSpent in real-time
        // Each visit is independent, no accumulation
        this.currentPage.timeSpent = this.currentPageTime;
        
        this.currentPage.scrollDepth = this.scrollDepth;
        this.currentPage.interactions = this.getTotalActions();
        this.currentPage.userActions = this.getCurrentPageActions() || undefined;
      }
      this.updateActivityData();
      
      // Store complete tracking data every 5 minutes
      if (Date.now() - this.lastDataSync > 300000) { // 5 minutes
        this.storeCompleteTrackingData();
        this.lastDataSync = Date.now();
      }
    }, 1000); // Update every second instead of 30 seconds
  }

  private startDataSync(): void {
    this.dataSyncInterval = setInterval(async () => {
      await this.updateActivityData();
    }, this.DATA_SYNC_INTERVAL * 3); // Reduce frequency by 3x (every 3 minutes instead of 1 minute)
  }

  private async updateActivityData(): Promise<void> {
    const userData = await this.getUserIdentificationData();
    const session = ssoSessionManager.getCurrentSession();

    // Calculate summary data
    const pageVisits = this.getPageVisits();
    const summary = this.calculatePageSummary(pageVisits);

    this.activityData = {
      sessionId: session?.sessionId || userData.sessionId,
      user_id: session?.user_id || userData.user_id,
      subscriber_id: session?.subscriber_id || userData.subscriber_id,
      deviceId: session?.deviceId || userData.deviceId,
      accessToken: session?.accessToken || userData.accessToken || undefined,
      cookieToken: userData.cookieToken,
      domain: session?.domain || userData.domain,
      siteType: session?.siteType || userData.siteType,
      startTime: session?.lastRenewed || Date.now(),
      lastActivity: Date.now(),
      totalTimeSpent: summary.totalTimeSpent,
      pageVisits: pageVisits,
      totalPages: summary.totalPages,
      averageTimePerPage: summary.averageTimePerPage,
      mostVisitedPage: summary.mostVisitedPage,
      engagementScore: summary.engagementScore,
      deviceInfo: this.deviceInfo || undefined,
    };

    // console.log('📊 Activity data updated:', this.activityData);
  }


  private calculatePageSummary(pageVisits: PageVisit[]) {
    const totalTimeSpent = pageVisits.reduce((sum, visit) => sum + (visit.timeSpent || 0), 0);
    const totalPages = pageVisits.length;
    const averageTimePerPage = totalPages > 0 ? totalTimeSpent / totalPages : 0;
    
    // Find most visited page
    const pageCounts = pageVisits.reduce((counts, visit) => {
      counts[visit.pageId] = (counts[visit.pageId] || 0) + 1;
      return counts;
    }, {} as Record<string, number>);
    
    const mostVisitedPage = Object.keys(pageCounts).reduce((a, b) => 
      pageCounts[a] > pageCounts[b] ? a : b, '');
    
    // Calculate engagement score (0-100)
    const totalInteractions = pageVisits.reduce((sum, visit) => sum + visit.interactions, 0);
    const engagementScore = Math.min(100, Math.round((totalInteractions / totalPages) * 10));

    return {
      totalTimeSpent,
      totalPages,
      averageTimePerPage,
      mostVisitedPage,
      engagementScore
    };
  }

  // ============================================================================
  // API INTEGRATION
  // ============================================================================

  /**
   * Store page visit data via API service
   */
  private async storePageVisitData(): Promise<void> {
    try {
      if (this.currentPage) {
        // Add formatted time object
        const pageVisitWithTime = {
          ...this.currentPage,
          timeSpentObject: this.formatTimeAsObject(this.currentPage.timeSpent || 0)
        };
        
        // Get host from current page domain
        const host = this.currentPage.domain || window.location.hostname;
        
        // Get subscriber login token
        const token = this.getSubscriberLoginToken() || this.currentPage.accessToken || '';
        
        await userTrackingService.storePageVisit(pageVisitWithTime, host, token);
      }
    } catch (error) {
      console.error('❌ Error storing page visit data:', error);
    }
  }

  /**
   * Store user interaction data via API service
   */
  private async storeUserInteractionData(interaction: UserInteraction): Promise<void> {
    try {
      // Get host from interaction data or current page
      const host = interaction.domain || this.currentPage?.domain || window.location.hostname;
      
      // Get subscriber login token
      const token = this.getSubscriberLoginToken() || interaction.accessToken || this.currentPage?.accessToken || '';
      
      await userTrackingService.storeUserInteraction(interaction, host, token);
    } catch (error) {
      console.error('❌ Error storing user interaction data:', error);
    }
  }

  /**
   * Store complete tracking data via API service
   */
  public async storeCompleteTrackingData(): Promise<void> {
    try {
      const trackingData = this.getTrackingData();
      
      // Get host from current page or window location
      const host = this.currentPage?.domain || window.location.hostname;
      
      // Get subscriber login token
      const token = this.getSubscriberLoginToken() || this.currentPage?.accessToken || '';
      
      await userTrackingService.storeTrackingData(trackingData, host, token);
    } catch (error) {
      console.error('❌ Error storing complete tracking data:', error);
    }
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  public getCurrentPage(): PageVisit | null {
    return this.currentPage;
  }

  public getCurrentPageTime(): number {
    if (!this.currentPage) return 0;
    return this.currentPageTime;
  }

  public getCurrentPageVisitNumber(): string {
    if (!this.currentPage) return '';
    return this.currentPage.pageVisitNumber;
  }

  public getTotalPageVisits(): number {
    return this.pageVisitCounter;
  }

  public formatTimeAsObject(milliseconds: number): { m: number; s: number } {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    return {
      m: minutes,
      s: remainingSeconds
    };
  }

  public getPageVisits(): PageVisit[] {
    return this.pageVisits;
  }

  public getUserActivity(): UserActivity | null {
    return this.activityData;
  }

  public getInteractions(): UserInteraction[] {
    return this.interactions;
  }

  public getTrackingData(): TrackingData {
    const allPageVisits = [...this.getPageVisits()];
    
    // Add current page if it exists (for real-time tracking)
    if (this.currentPage) {
      // Always add current page as a new entry
      allPageVisits.push({
        ...this.currentPage,
        timeSpent: this.currentPageTime, // Use real-time current time
        isActive: true
      });
      
      // console.log(`📊 Current page (Visit UUID: ${this.currentPage.pageVisitNumber} - real-time): ${this.currentPage.pageUrl} - Time: ${Math.round(this.currentPageTime / 1000)}s`);
    }
    
    const userActivity = this.getUserActivity();
    const summary = this.calculatePageSummary(allPageVisits);

    // Calculate comprehensive page analytics
    const pageAnalytics: PageAnalytics[] = this.calculateComprehensivePageAnalytics(allPageVisits);

    return {
      pageVisits: allPageVisits.map(visit => ({
        ...visit,
        timeSpentObject: this.formatTimeAsObject(visit.timeSpent || 0)
      })),
      userActivity,
      pageAnalytics,
      sessionSummary: summary,
      timestamp: Date.now()
    };
  }

  public forceUpdateActivityData(): void {
    this.updateActivityData();
  }

  public forcePageTracking(): void {
    // Force track the current page - useful when route changes aren't detected
    // console.log('🔄 Force tracking current page:', window.location.href);
    this.trackPageEntry();
  }

  public forceUserChangeCheck(): void {
    // Manually trigger user change check - useful when you know user has changed
    // console.log('👤 Manually checking for user change');
    this.checkForUserChange();
  }

  public resetForNewUser(): void {
    // Manually reset tracking for new user - useful when you know user has changed
    // console.log('👤 Manually resetting tracking for new user');
    this.storeCurrentPageDataBeforeUserChange();
    this.resetTrackingForNewUser();
    this.updateCurrentUserIds();
    this.startFreshTrackingForNewUser();
  }

  public async forceSyncUserId(subscriber_id: number): Promise<string | null> {
    // Force sync user_id from server - useful when you know user has changed
    // console.log(`🌐 Force syncing user_id for subscriber_id: ${subscriber_id}`);
    
    try {
      const userId = await this.getUserIdFromServer(subscriber_id);
      if (userId) {
        const userKey = `pm_user_id_${subscriber_id}`;
        localStorage.setItem(userKey, userId);
        sessionStorage.setItem(userKey, userId);
        // console.log(`🌐 Force sync successful: user_id ${userId} for subscriber_id ${subscriber_id}`);
        return userId;
      }
    } catch (error) {
      console.error('🌐 Force sync failed:', error);
    }
    
    return null;
  }

  public getPageAnalytics(): PageAnalytics[] {
    // Get comprehensive analytics for all pages
    const allPageVisits = this.getPageVisits();
    return this.calculateComprehensivePageAnalytics(allPageVisits);
  }

  public getPageAnalyticsById(pageId: string): PageAnalytics | null {
    // Get analytics for a specific page
    const allAnalytics = this.getPageAnalytics();
    return allAnalytics.find(analytics => analytics.pageId === pageId) || null;
  }

  public getPageAnalyticsByUrl(pageUrl: string): PageAnalytics | null {
    // Get analytics for a specific page URL
    const allAnalytics = this.getPageAnalytics();
    return allAnalytics.find(analytics => analytics.pageUrl === pageUrl) || null;
  }

  public getOverallAnalytics(): {
    totalPages: number;
    totalVisits: number;
    totalTimeSpent: number;
    averageBounceRate: number;
    averageEngagementRate: number;
    averageSessionDuration: number;
    totalUniqueVisitors: number;
  } {
    const allAnalytics = this.getPageAnalytics();
    
    if (allAnalytics.length === 0) {
      return {
        totalPages: 0,
        totalVisits: 0,
        totalTimeSpent: 0,
        averageBounceRate: 0,
        averageEngagementRate: 0,
        averageSessionDuration: 0,
        totalUniqueVisitors: 0
      };
    }

    const totalVisits = allAnalytics.reduce((sum, analytics) => sum + analytics.totalVisits, 0);
    const totalTimeSpent = allAnalytics.reduce((sum, analytics) => sum + analytics.totalTimeSpent, 0);
    const totalBounceRate = allAnalytics.reduce((sum, analytics) => sum + analytics.bounceRate, 0);
    const totalEngagementRate = allAnalytics.reduce((sum, analytics) => sum + analytics.engagementRate, 0);
    const totalSessionDuration = allAnalytics.reduce((sum, analytics) => sum + analytics.averageSessionDuration, 0);
    const uniqueVisitors = new Set(allAnalytics.flatMap(analytics => [analytics.user_id]).filter(Boolean)).size;

    return {
      totalPages: allAnalytics.length,
      totalVisits,
      totalTimeSpent,
      averageBounceRate: totalBounceRate / allAnalytics.length,
      averageEngagementRate: totalEngagementRate / allAnalytics.length,
      averageSessionDuration: totalSessionDuration / allAnalytics.length,
      totalUniqueVisitors: uniqueVisitors
    };
  }

  public destroy(): void {
    // Store final tracking data before cleanup
    this.storeCompleteTrackingData();
    
    // Force flush any pending API data
    const host = this.currentPage?.domain || window.location.hostname;
    const token = this.getSubscriberLoginToken() || this.currentPage?.accessToken || '';
    userTrackingService.forceFlush(host, token);
    
    // Clean up intervals
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
    if (this.dataSyncInterval) {
      clearInterval(this.dataSyncInterval);
    }
    
    // Stop API service sync
    userTrackingService.stopPeriodicSync();
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

export const userTracking = new UserTracking();

// Auto-initialize on page load
if (typeof window !== 'undefined') {
  userTracking.trackPageEntry();
}
